import { moment } from '@feathr/hooks';
import type { TConstraints, TOptionalConstraint } from '@feathr/rachis';

import { MailrailBaseEmailCampaign } from './mailrail_email_base';
import type { IEmailTrigger, IMailrailAutoSendEmailCampaign } from './types';
import { CampaignState } from './types';

type PartialAttrs = Partial<IMailrailAutoSendEmailCampaign>;

export class MailrailAutoSendEmailCampaign<
  T extends IMailrailAutoSendEmailCampaign = IMailrailAutoSendEmailCampaign,
> extends MailrailBaseEmailCampaign<T> {
  public override get constraints(): TConstraints<T> {
    return {
      ...super.constraints,
      bulk_send_enabled: {
        presence: {
          allowEmpty: false,
        },
      },
      bulk_send_timing: {
        exclusion: (_: string, attributes: PartialAttrs): TOptionalConstraint => {
          if (attributes.repeat_allowed && attributes.bulk_send_enabled) {
            return {
              within: ['end'],
              message:
                '^Post-campaign bulk send is incompatible with the "Send every time" setting',
            };
          }
          return undefined;
        },
        presence: {
          allowEmpty: false,
        },
      },
      date_end: {
        presence: {
          allowEmpty: false,
          message: "^End date can't be blank",
        },
        datetime: (_: string, attributes: PartialAttrs): TOptionalConstraint => {
          return {
            earliest: moment.utc(attributes.date_start).add(1, 'day').startOf('minute'),
            latest: moment.utc(attributes.date_start).add(1, 'year').endOf('minute'),
            message: `^End time must be at least 24 hours after start time, but no more than a year later`,
          };
        },
      },
      date_start: {
        // Time subtype autosends require a date_start because its time component is used as the daily send time
        presenceUnless: {
          allowEmpty: false,
          message: "^Time of day can't be blank",
          unless: ({ subtype }) => subtype !== 'time',
        },
        datetime: (value: string, attributes: PartialAttrs): TOptionalConstraint => {
          if (!value) {
            return undefined;
          }
          if (
            attributes.state &&
            [CampaignState.Draft, CampaignState.Stopped].includes(attributes.state)
          ) {
            return {
              earliest: moment.utc().add(15, 'minutes'),
              message: `^Start time must be at least 15 minutes in the future`,
            };
          }
          return undefined;
        },
      },
      delay_seconds: {
        presence: {
          allowEmpty: false,
        },
      },
      segments_included: {
        length: (_: string[], attributes: PartialAttrs): TOptionalConstraint => {
          if (attributes.bulk_send_enabled && attributes.bulk_send_timing === 'end') {
            return {
              minimum: 1,
              tooShort: '^A post-campaign bulk send requires at least one included group',
            };
          }
          return undefined;
        },
        list: {
          presence: {
            allowEmpty: false,
            message: '^Finish selecting a group or remove the placeholder group.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Groups must be unique.',
        },
      },
      predicates(value: IEmailTrigger[]): TOptionalConstraint {
        if (value.length < 1) {
          return {
            length: { minimum: 1, message: '^You must have at least one trigger' },
          };
        }

        // The comparisons "exists" and "not exists" do not require a value.
        const filteredActions = value.filter(
          ({ comparison }) => !(['exists', 'nexists'] as unknown[]).includes(comparison),
        );

        // True and false are valid values, so we only want to check for undefined, empty string, and null.
        if (
          filteredActions.some(({ value }) => (['', undefined, null] as unknown[]).includes(value))
        ) {
          return {
            array: {
              value: {
                presence: { allowEmpty: false, message: '^Trigger value cannot be blank' },
              },
            },
          };
        }

        return undefined;
      },
      repeat_allowed: {
        exclusion: (_: boolean, attributes: PartialAttrs): TOptionalConstraint => {
          if (attributes.bulk_send_timing === 'end' && attributes.bulk_send_enabled) {
            return {
              within: [true],
              message: '^Repeat sending is not compatible with the post-campaign bulk send option',
            };
          }
          return undefined;
        },
        presence: {
          allowEmpty: false,
          message:
            "^Choose whether you'd like your email to be sent only once or each time the action is performed",
        },
      },
      repeat_cooldown_seconds: {
        presenceUnless: {
          allowEmpty: false,
          unless: ({ repeat_allowed }) => repeat_allowed === false,
        },
      },
    };
  }

  public override getDefaults(): Partial<T> {
    return {
      ...super.getDefaults(),
      delay_seconds: 1800,
      predicates: [],
      predicates_mode: 'match_all',
      subtype: 'activity',
    };
  }
}
